import React, { useState } from "react";
import { ThumbsUp, ThumbsDown, Check, MessageSquare, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Feedback utente Foundation su un articolo del Centro Guida.
 *
 * Due voti possibili: "helpful" | "not_helpful".
 * Se "not_helpful", chiediamo (opzionalmente) un commento in un'area espandibile.
 *
 * Props:
 *  - appKey (obbligatorio)
 *  - articleId (obbligatorio)
 *  - initialSummary? {helpful, not_helpful, total, my_verdict}
 *
 * Il componente e' idempotente: se l'utente ha gia' votato, mostra lo stato
 * corrente e permette di cambiare voto.
 */
export function GuideFeedback({ appKey, articleId, initialSummary }) {
  const [summary, setSummary] = useState(
    initialSummary || { helpful: 0, not_helpful: 0, total: 0, my_verdict: null },
  );
  const [submitting, setSubmitting] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState("");
  const [thanks, setThanks] = useState(false);

  const submit = async (verdict, comm) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/guide/feedback", {
        app: appKey,
        article_id: articleId,
        verdict,
        comment: comm || null,
      });
      if (data?.summary) setSummary(data.summary);
      setThanks(true);
      setTimeout(() => setThanks(false), 2200);
      if (verdict === "helpful") setShowCommentBox(false);
    } catch {
      // best-effort silenzioso: la UI mostra lo stato precedente
    } finally {
      setSubmitting(false);
    }
  };

  const onHelpful = () => submit("helpful", null);
  const onNotHelpful = () => {
    setShowCommentBox(true);
    submit("not_helpful", null);
  };
  const onSendComment = () => submit("not_helpful", comment.trim() || null);

  const my = summary.my_verdict;

  return (
    <div
      className="mt-3 rounded-md border border-suite-neutral-100 bg-suite-neutral-50 p-2.5"
      data-testid={`guide-feedback-${articleId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold text-suite-neutral-600">
          Ti e' stato utile?
        </span>
        <button
          type="button"
          onClick={onHelpful}
          disabled={submitting}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
            my === "helpful"
              ? "border-suite-success/40 bg-suite-success/10 text-suite-success"
              : "border-suite-neutral-200 bg-white text-suite-neutral-600 hover:border-suite-success/40 hover:bg-suite-success/5 hover:text-suite-success"
          }`}
          data-testid={`guide-feedback-helpful-${articleId}`}
        >
          {my === "helpful" ? <Check size={12} /> : <ThumbsUp size={12} />}
          Mi e' stato utile
          {summary.helpful > 0 && (
            <span className="ml-1 text-suite-neutral-400">({summary.helpful})</span>
          )}
        </button>
        <button
          type="button"
          onClick={onNotHelpful}
          disabled={submitting}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition ${
            my === "not_helpful"
              ? "border-suite-warning/40 bg-suite-warning/10 text-suite-warning"
              : "border-suite-neutral-200 bg-white text-suite-neutral-600 hover:border-suite-warning/40 hover:bg-suite-warning/5 hover:text-suite-warning"
          }`}
          data-testid={`guide-feedback-not-helpful-${articleId}`}
        >
          {my === "not_helpful" ? <Check size={12} /> : <ThumbsDown size={12} />}
          Non ho risolto
          {summary.not_helpful > 0 && (
            <span className="ml-1 text-suite-neutral-400">({summary.not_helpful})</span>
          )}
        </button>
        {submitting && (
          <Loader2 size={12} className="animate-spin text-suite-neutral-400" />
        )}
        {thanks && !submitting && (
          <span className="text-[11px] text-suite-success">Grazie per il tuo feedback!</span>
        )}
      </div>

      {showCommentBox && my === "not_helpful" && (
        <div className="mt-2 space-y-1.5">
          <label
            className="flex items-center gap-1 text-[10px] uppercase tracking-[0.1em] text-suite-neutral-500"
            htmlFor={`fb-comment-${articleId}`}
          >
            <MessageSquare size={11} />
            Cosa manca? (facoltativo)
          </label>
          <textarea
            id={`fb-comment-${articleId}`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Segnala cosa non hai trovato o cosa non e' chiaro..."
            className="w-full rounded-md border border-suite-neutral-200 bg-white p-2 text-xs text-suite-neutral-800 placeholder:text-suite-neutral-400 focus:border-suite-primary/50 focus:outline-none"
            data-testid={`guide-feedback-comment-${articleId}`}
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-suite-neutral-400">
              {comment.length}/500
            </span>
            <button
              type="button"
              onClick={onSendComment}
              disabled={submitting}
              className="rounded-md bg-suite-primary px-2.5 py-1 text-[11px] font-medium text-white hover:bg-suite-primary/90 disabled:opacity-50"
              data-testid={`guide-feedback-send-${articleId}`}
            >
              Invia
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
